/**
 * Build-time configuration.
 *
 * One place per tier, matching the backend's `config.py`: a setting is read
 * here and imported from here, never read from `import.meta.env` at a call
 * site. Otherwise "where does this value come from" is a grep across the app.
 */

/**
 * Where the staff sign-in page lives.
 *
 * Configurable because it is not linked from anywhere in the interface, and a
 * path nobody advertises should not be one an attacker can assume. Two things
 * this is **not**, and both matter:
 *
 * - It is not a security control. Moving the door does not lock it. What
 *   protects these accounts is Argon2id hashing, per-account login throttling
 *   with a time-boxed lock, server-side refresh revocation, and a role check on
 *   every admin route. This only keeps the page out of the way of drive-by
 *   scanners and casual visitors.
 * - It is deliberately **not** published in `robots.txt`. That file lists every
 *   private prefix as a `Disallow:` line, and adding this one would announce
 *   the path to anyone who reads it — which is everyone. The page stays out of
 *   search by not being linked, which is what actually keeps a client-rendered
 *   route undiscovered.
 *
 * Vite inlines this at build time, so changing it needs a rebuild, not a
 * restart.
 */
function loginPath(): string {
  const configured = (import.meta.env.VITE_LOGIN_PATH ?? '').trim()
  if (!configured) return '/login'
  // A path that does not start with a slash would silently become relative and
  // resolve differently from every page it is linked on.
  return configured.startsWith('/') ? configured.replace(/\/+$/, '') : `/${configured}`
}

export const LOGIN_PATH = loginPath()

export const APP_NAME = (import.meta.env.VITE_APP_NAME ?? '').trim() || 'NaijaIndex'
